/* eslint-disable */

const CONFIG = {
  // attributes: true,
  childList: true,
  subtree: true,
};
const NODE_ELEMENT = 1;
const OBSERVER_ID = "YGT_MutationObserver";
const mutations = {};
/** @type {string[]} */
let mutationsKeys = [];

/**
 * Explicitly destroys the observer.
 */
function destroy() {
  let mutationObserver = window[OBSERVER_ID];

  if (mutationObserver) {
    delete window[OBSERVER_ID];

    mutationObserver.takeRecords();
    mutationObserver.disconnect();
    mutationObserver = null;
  }
}

/**
 * Runs the stack of methods assigned to the node.
 *
 * Each method instantiation is contained within a `requestAnimationFrame` call
 * to reduce the immediacy that otherwise might block the main thread unnecessarily.
 *
 * @param {Node} node The mutated node.
 */
function process(node) {
  mutationsKeys.forEach((selector) => {
    const methods = mutations[selector] || [];

    if (methods.length === 0) {
      return;
    }

    node.querySelectorAll(selector).forEach((element) => {
      methods.forEach((method) => {
        window.requestAnimationFrame(() => {
          method(element);
        });
      });
    });
  });
}

/**
 * Processes the provided mutations collection.
 *
 * @param {MutationRecord[]} entries The collection of mutations.
 */
function onmutation(entries) {
  entries.forEach(({ addedNodes }) => {
    [].slice
      .call(addedNodes)
      .filter(({ nodeType }) => nodeType === NODE_ELEMENT)
      .forEach(process);
  });
}

/**
 * Adds a new MutationObserver and a pointer on the `window` object.
 *
 * This is run every time `add` is called and sets up the singleton observer.
 */
function init() {
  if (!window[OBSERVER_ID]) {
    const mutationObserver = new MutationObserver(onmutation);

    mutationObserver.observe(document.body, CONFIG);
    window[OBSERVER_ID] = mutationObserver;

    window.addEventListener("beforeunload", destroy);
  }
}

/**
 * Adds a reference via `selector` to run a specified method.
 *
 * @param {string} selector The selector for the relevant node(s).
 * @param {function} callback The method to add to the stack.
 *
 * @example
 * // Logs source of an image when dynamically added
 * add("img", function (img) { console.log(img.src); });
 */
function add(selector, callback) {
  const processes = mutations[selector] || [];

  if (!processes.length) {
    mutationsKeys.push(selector);
  }

  processes.push(callback);
  mutations[selector] = processes;
  init();
}

/**
 * Removes a reference via `selector` to run a specified method.
 *
 * If `callback` is not specified, the entire stack referenced by `selector` is removed.
 *
 * @param {string} selector The selector for the relevant node(s).
 * @param {function} [callback] The method to remove from the stack (optional).
 */
function remove(selector, callback) {
  const processes = (mutations[selector] || []).filter(
    (item) => item !== callback,
  );

  if (processes.length) {
    mutations[selector] = processes;
  } else {
    delete mutations[selector];
    mutationsKeys = mutationsKeys.filter((key) => key !== selector);
  }
}

/**
 * Return API
 *
 * @example
 * import mutations from "./mutations";
 *
 * const selector = "img";
 * const method = (img) => { console.log(img.src); };
 *
 * mutations.add(selector, method);
 */
export default {
  add,
  destroy,
  remove,
};
